import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Flame, Loader2, Minus, Plus } from "lucide-react";
import { http } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { useRetailCart } from "../contexts/RetailCartContext";
import { ProductImage } from "../components/ProductImage";

export default function ProductDetail() {
  const location = useLocation();
  const id = location.pathname.split("/").pop();
  const [p, setP] = useState(null);
  const { user } = useAuth();
  const { add: addCase } = useCart();
  const { add: addRetail } = useRetailCart();
  const [qty, setQty] = useState(1);
  const [retailQty, setRetailQty] = useState(1);
  const nav = useNavigate();

  useEffect(() => { http.get(`/products/${id}`).then(({ data }) => setP(data)); }, [id]);
  if (!p) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

  const canBuyWholesale = user && user.role && (user.is_approved || user.role === "admin") && !p.coming_soon;

  return (
    <div className="bg-[#FFFDF8] py-16" data-testid="product-root">
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-16 items-start">
        <div className="aspect-square bg-[#FDF4E3] border border-[#E5D8C1] flex items-center justify-center overflow-hidden relative">
          <ProductImage p={p} />
        </div>
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[#D32F2F] mb-3">{p.tagline}</div>
          <h1 className="font-heading text-5xl md:text-6xl uppercase leading-[0.95]">{p.name}</h1>
          {p.coming_soon && (
            <div className="inline-block mt-4 bg-[#FFB300] text-[#1A1A1A] text-xs uppercase tracking-widest px-3 py-1.5 font-heading">Coming soon</div>
          )}
          <p className="text-lg text-[#5C5A56] leading-relaxed mt-6">{p.description}</p>
          <ul className="mt-6 space-y-2">
            {p.highlights.map((h) => (
              <li key={h} className="flex items-center gap-2 text-[#1A1A1A]"><Flame size={16} className="text-[#D32F2F]" /> {h}</li>
            ))}
          </ul>

          {/* Retail buy — no auth */}
          <div className="mt-10 bg-[#FDF4E3] border border-[#E5D8C1] p-6" data-testid="product-retail-block">
            <div className="flex items-baseline justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#5C5A56]">Retail</div>
                <div className="font-heading text-4xl text-[#D32F2F]">${p.retail_msrp?.toFixed(2)} <span className="text-sm text-[#5C5A56] uppercase tracking-widest ml-1">/ {p.size_oz} oz chilito candy</span></div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="flex items-center border-2 border-[#1A1A1A]">
                <button disabled={!!p.coming_soon} onClick={() => setRetailQty(Math.max(1, retailQty - 1))} className="w-10 h-10 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white disabled:opacity-40" data-testid="product-retail-qty-minus"><Minus size={14} /></button>
                <input value={retailQty} onChange={(e) => setRetailQty(Math.max(1, parseInt(e.target.value) || 1))} disabled={!!p.coming_soon} className="w-12 h-10 text-center border-x-2 border-[#1A1A1A] bg-transparent" data-testid="product-retail-qty-input" />
                <button disabled={!!p.coming_soon} onClick={() => setRetailQty(retailQty + 1)} className="w-10 h-10 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white disabled:opacity-40" data-testid="product-retail-qty-plus"><Plus size={14} /></button>
              </div>
              <button
                disabled={!!p.coming_soon}
                onClick={() => { addRetail(p.id, retailQty); nav("/cart"); }}
                className="flex-1 min-w-[200px] bg-[#D32F2F] text-white hover:bg-[#B71C1C] px-6 py-3 uppercase tracking-widest font-heading transition-transform hover:-translate-y-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="product-retail-add"
              >
                {p.coming_soon ? "Coming soon" : `Add ${retailQty} chilito${retailQty > 1 ? "s" : ""} to bag`}
              </button>
            </div>
          </div>

          {/* Wholesale */}
          <div className="mt-6 border border-[#1A1A1A] p-6" data-testid="product-wholesale-block">
            <div className="flex items-baseline justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#5C5A56]">Wholesale (approved buyers)</div>
                <div className="font-heading text-4xl">${p.case_price.toFixed(2)} <span className="text-sm text-[#5C5A56] uppercase tracking-widest ml-1">/ case of {p.case_units}</span></div>
              </div>
            </div>
            {canBuyWholesale ? (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <div className="flex items-center border-2 border-[#1A1A1A]">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-[#FFFDF8]" data-testid="product-qty-minus"><Minus size={14} /></button>
                  <input value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-12 h-10 text-center border-x-2 border-[#1A1A1A] bg-transparent" data-testid="product-qty-input" />
                  <button onClick={() => setQty(qty + 1)} className="w-10 h-10 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-[#FFFDF8]" data-testid="product-qty-plus"><Plus size={14} /></button>
                </div>
                <button onClick={() => { addCase(p.id, qty); nav("/portal"); }} className="flex-1 min-w-[200px] bg-[#1A1A1A] text-white hover:bg-[#D32F2F] px-6 py-3 uppercase tracking-widest font-heading" data-testid="product-add-to-cart">
                  Add {qty} case{qty > 1 ? "s" : ""} to portal
                </button>
              </div>
            ) : (
              <div className="mt-3 text-sm text-[#5C5A56]">
                Approved buyers only. <Link to="/wholesale" className="text-[#D32F2F] font-semibold">Apply here</Link> · <Link to="/login" className="underline">or log in</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
